import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import useIndex from '../../../hooks/useIndex'
import validErrors from '../../../hooks/validErrors'
import GiftsMake from '../../ganacol/gifts/GiftsMake'
import Gifts from '../../ganacol/gifts/Gifts'
import { getAllUserRoutesType } from '../../../data/api/users/UserRoutes'
import { Modal } from '@mui/material'

const componentMap = {
    GiftsMake: GiftsMake,
    Gifts: Gifts,
};

const SEVERITY_COLOR = {
    success: '#1e6a36',
    info: '#10285f',
    warning: '#8a5a00',
    error: '#b71c1c',
};
const SEVERITY_BG = {
    success: 'rgba(46, 125, 50, 0.16)',
    info: 'rgba(16, 40, 95, 0.18)',
    warning: 'rgba(214, 170, 58, 0.22)',
    error: 'rgba(211, 47, 47, 0.18)',
};

const fmtRelativeTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diffSec = Math.max(0, (Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return 'hace unos segundos';
    if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
    if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)} h`;
    if (diffSec < 86400 * 7) return `hace ${Math.floor(diffSec / 86400)} d`;
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const NotificationBar = ({
    setActiveNot,
    activeNot,
    notifications,
}) => {
    const { token } = useIndex();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [optionSelect, setOptionSelect] = useState('GiftsMake');
    const SelectedComponent = componentMap[optionSelect];

    const {
        items = [],
        loading = false,
        unreadCount = 0,
        pendingGiftsCount = 0,
        loadFeed,
        markOneRead,
        markAllRead,
    } = notifications ?? {};

    useEffect(() => {
        getOptions();
    }, []);

    // Cargamos el feed cada vez que el panel se abre — así no traemos
    // datos que no se van a mirar y siempre tenemos lo más reciente.
    useEffect(() => {
        if (activeNot && typeof loadFeed === 'function') {
            loadFeed();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeNot]);

    const handleOpen = (e, opc) => {
        e.preventDefault();
        setOptionSelect(opc);
        setOpen(true);
    }
    const handleClose = () => {
        setOpen(false);
    }

    const getOptions = async () => {
        try {
            const { data } = await getAllUserRoutesType(token, 3);

            if (data?.success) {
                setOptions(data?.data);
            }
        } catch (error) {
            validErrors(error);
        }
    }

    const handleNotifClick = async (n) => {
        if (!n) return;
        if (!n.read_at) await markOneRead?.(n.id);
        const link = n.data?.link;
        if (link) {
            setActiveNot(false);
            navigate(link);
        }
    };

    return (
        <NotificationContainer id="barNot" className={`animate__animated ${activeNot ? 'animate__slideInRight' : 'animate__slideOutRight'}`}>
            <div className={`menu-notif`}>
                <div className="cont-men-not">
                    {/* HEADER */}
                    <div className="titular-notif">
                        <p className="titulo-notif-mnu">Notificaciones</p>
                        <i className="fa-solid fa-xmark" id="cerrar_notificaciones" onClick={() => setActiveNot(false)}></i>
                    </div>

                    {/* ACCESOS RÁPIDOS (los user_routes type=3 que ya existían) */}
                    {options?.length > 0 && (
                        <div className="block">
                            <p className="block-title">Accesos rápidos</p>
                            <nav className="opc-mnu">
                                {options.map((item) => {
                                    // Hoy el único acceso con "pendiente"
                                    // es Regalos. Si llegan más casos, se
                                    // puede mapear a un dict por link.
                                    const isGifts = item?.link === 'Gifts';
                                    const hasPending = isGifts && pendingGiftsCount > 0;
                                    return (
                                        <button
                                            key={item.id}
                                            onClick={(e) => handleOpen(e, item?.link)}
                                            className={`opc-mnu-not ${hasPending ? 'has-pending' : ''}`}
                                        >
                                            <i className={item?.icon}></i>
                                            <span className="opc-label">{item?.label}</span>
                                            {hasPending && (
                                                <span
                                                    className="pending-badge"
                                                    aria-label={`${pendingGiftsCount} pendientes`}
                                                >
                                                    {pendingGiftsCount}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </nav>
                        </div>
                    )}

                    {/* FEED REAL DE NOTIFICACIONES */}
                    <div className="block feed-block">
                        <div className="block-head">
                            <p className="block-title">
                                Mis notificaciones
                                {unreadCount > 0 && <span className="unread-pill">{unreadCount}</span>}
                            </p>
                            {unreadCount > 0 && (
                                <button className="link-btn" onClick={markAllRead}>
                                    Marcar todas
                                </button>
                            )}
                        </div>

                        <div className="feed-list">
                            {loading && items.length === 0 ? (
                                <div className="feed-empty">Cargando…</div>
                            ) : items.length === 0 ? (
                                <div className="feed-empty">
                                    <i className="fa-regular fa-bell-slash" />
                                    <span>Sin notificaciones por ahora.</span>
                                </div>
                            ) : (
                                items.map((n) => {
                                    const sev = n.data?.severity ?? 'info';
                                    const isUnread = !n.read_at;
                                    return (
                                        <button
                                            key={n.id}
                                            className={`feed-item ${isUnread ? 'unread' : ''}`}
                                            onClick={() => handleNotifClick(n)}
                                            style={{ '--sev-color': SEVERITY_COLOR[sev] ?? SEVERITY_COLOR.info,
                                                '--sev-bg': SEVERITY_BG[sev] ?? SEVERITY_BG.info }}
                                        >
                                            <span className="feed-icon">
                                                <i className={n.data?.icon ?? 'fa-solid fa-bell'} />
                                            </span>
                                            <span className="feed-body">
                                                <span className="feed-title">{n.data?.title ?? 'Notificación'}</span>
                                                <span className="feed-text">{n.data?.body ?? ''}</span>
                                                <span className="feed-time">{fmtRelativeTime(n.created_at)}</span>
                                            </span>
                                            {isUnread && <span className="feed-dot" />}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* FOOTER */}
                    <div className="foot-not">
                        <i className="fa-brands fa-css3-alt"></i>
                        <i className="fa-brands fa-html5"></i>
                        <i className="fa-brands fa-square-js"></i>
                        <i className="fa-brands fa-laravel"></i>
                        <i className="fa-brands fa-react"></i>
                        <i className="fa-brands fa-php"></i>
                        <i className="fa-solid fa-code"></i>
                    </div>
                </div>
            </div>

            <Modal
                open={open}
                onClose={handleClose}
            >
                <div>
                    {SelectedComponent ? (
                        <SelectedComponent handleClose={handleClose} />
                    ) : (
                        <div>Componente no encontrado</div>
                    )}
                </div>
            </Modal>
        </NotificationContainer>
    )
}

export default NotificationBar

const NotificationContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;

    .menu-notif {
        position: absolute;
        top: 0;
        right: 0;
        width: 300px;
        max-width: 100%;
        height: 100%;

        background: linear-gradient(180deg, #10285f 0%, #1a2f5c 48%, #0f224d 100%);
        border-left: 2px solid rgba(214, 170, 58, 0.6);
        box-shadow: -10px 0 28px rgba(0, 0, 0, 0.25);
        /* Bloqueamos el eje horizontal a nivel del panel: los hovers
           internos hacen translateX(3-4px) y eso, sumado al ancho
           completo de los items, dispara una barra horizontal en el
           contenedor padre. Con esto solo queda scroll vertical. */
        overflow-x: hidden;

        .cont-men-not {
            position: relative;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            min-height: 0;
            overflow-x: hidden;
        }

        .block {
            padding: 12px 12px 4px;
        }
        .block.feed-block {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
            padding-bottom: 8px;
        }
        .block-head {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
        }
        .block-title {
            margin: 0 0 6px;
            font-family: 'Lato';
            font-size: 0.78rem;
            font-weight: 800;
            letter-spacing: 0.6px;
            text-transform: uppercase;
            color: rgba(248, 244, 232, 0.65);
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .unread-pill {
            background: #d32f2f;
            color: #fff;
            font-size: 0.7rem;
            font-weight: 800;
            padding: 1px 7px;
            border-radius: 999px;
            min-width: 18px;
            text-align: center;
        }
        .link-btn {
            background: transparent;
            border: none;
            color: #f3cd61;
            font-size: 0.78rem;
            font-weight: 700;
            cursor: pointer;
            padding: 2px 4px;
            transition: 0.2s ease;

            &:hover { color: #fff; }
        }

        /* FEED */
        .feed-list {
            flex: 1;
            overflow-y: auto;
            /* Importante: cuando se setea overflow-y, el browser pone
               overflow-x en 'auto' por defecto y aparece la barra al
               más mínimo overflow (típico con el translateX del hover).
               Forzar 'hidden' mantiene el feed limpio. */
            overflow-x: hidden;
            padding-right: 2px;
            display: flex;
            flex-direction: column;
            gap: 6px;

            &::-webkit-scrollbar { width: 5px; }
            &::-webkit-scrollbar-thumb {
                background: rgba(214, 170, 58, 0.7);
                border-radius: 999px;
            }
        }
        .feed-empty {
            padding: 24px 14px;
            border-radius: 12px;
            background: rgba(255, 255, 255, 0.04);
            border: 1px dashed rgba(214, 170, 58, 0.22);
            color: rgba(248, 244, 232, 0.65);
            font-size: 0.85rem;
            text-align: center;

            i { display: block; font-size: 1.5rem; margin-bottom: 6px; opacity: 0.7; }
        }
        .feed-item {
            position: relative;
            width: 100%;
            display: flex;
            align-items: flex-start;
            gap: 10px;
            padding: 10px 12px;
            text-align: left;
            background: rgba(255, 255, 255, 0.04);
            border: 1px solid rgba(214, 170, 58, 0.14);
            border-left: 3px solid var(--sev-color, #10285f);
            border-radius: 10px;
            cursor: pointer;
            color: #f8f4e8;
            transition: 0.2s ease;

            &:hover {
                background: rgba(255, 255, 255, 0.08);
                transform: translateX(3px);
            }
            &.unread {
                background: rgba(255, 255, 255, 0.08);
                border-color: rgba(214, 170, 58, 0.32);
            }

            .feed-icon {
                width: 32px;
                height: 32px;
                flex-shrink: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 9px;
                background: var(--sev-bg, rgba(16, 40, 95, 0.18));
                color: var(--sev-color, #f3cd61);
                font-size: 0.95rem;
            }
            .feed-body {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 2px;
                min-width: 0;
            }
            .feed-title {
                font-size: 0.9rem;
                font-weight: 800;
                color: #fff;
                line-height: 1.2;
            }
            .feed-text {
                font-size: 0.8rem;
                color: rgba(248, 244, 232, 0.78);
                line-height: 1.35;
                /* Limita a 3 líneas para que el feed no se desborde
                   con mensajes largos. */
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }
            .feed-time {
                font-size: 0.7rem;
                color: rgba(248, 244, 232, 0.5);
                margin-top: 2px;
            }
            .feed-dot {
                position: absolute;
                top: 12px;
                right: 12px;
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: #d32f2f;
            }
        }

        /* HEADER */
        .titular-notif {
            width: 100%;
            min-height: 54px;
            padding: 0 16px;

            display: flex;
            justify-content: space-between;
            align-items: center;

            background: rgba(255, 255, 255, 0.05);
            border-bottom: 1px solid rgba(214, 170, 58, 0.25);

            .titulo-notif-mnu {
                font-family: 'Lato';
                font-size: 1.2rem;
                letter-spacing: 0.5px;
                margin: 0;
                color: #f8f4e8;
                text-transform: uppercase;
            }

            i {
                font-size: 1.2rem;
                color: #f8f4e8;
                cursor: pointer;
                transition: 0.25s ease;

                &:hover {
                    color: #f3cd61;
                    transform: scale(1.1);
                }
            }
        }

        /* ACCESOS RÁPIDOS */
        .opc-mnu {
            display: flex;
            flex-direction: column;
            gap: 6px;
            padding: 0;

            .opc-mnu-not {
                width: 100%;
                min-height: 52px;
                display: flex;
                align-items: center;
                gap: 12px;

                padding: 0 14px;

                /* Reset del estilo nativo del <button>: si no se fija
                   background/border explícitos, el browser pinta el
                   chrome por defecto (gris claro + borde) y el texto
                   crema queda ilegible al abrir el panel. */
                background: rgba(255, 255, 255, 0.06);
                border: 1px solid rgba(214, 170, 58, 0.18);
                border-radius: 12px;
                text-align: left;
                cursor: pointer;
                text-decoration: none;

                color: #f8f4e8;
                font-family: 'Lato';
                font-size: 0.95rem;
                font-weight: 600;
                letter-spacing: 0.4px;
                transition: background 0.25s ease, transform 0.25s ease,
                            border-color 0.25s ease, color 0.25s ease;

                i {
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;

                    border-radius: 10px;
                    background: rgba(214, 170, 58, 0.2);
                    color: #f3cd61;
                    font-size: 1rem;
                }

                .opc-label {
                    flex: 1;
                }

                &:hover {
                    background: rgba(214, 170, 58, 0.16);
                    border-color: rgba(214, 170, 58, 0.5);
                    transform: translateX(4px);
                    color: #fff;
                }

                &:focus-visible {
                    outline: 2px solid rgba(214, 170, 58, 0.6);
                    outline-offset: 2px;
                }

                /* Estado "tiene pendientes": resalta la opción con un
                   acento dorado más intenso + ring pulsante para que el
                   usuario lo note al abrir el panel. */
                &.has-pending {
                    background: rgba(214, 170, 58, 0.18);
                    border-color: rgba(214, 170, 58, 0.65);
                    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.45);
                    animation: pendingPulse 2.2s ease-in-out infinite;

                    i {
                        background: rgba(214, 170, 58, 0.35);
                        color: #fff;
                    }
                }

                .pending-badge {
                    flex: none;
                    min-width: 22px;
                    height: 22px;
                    padding: 0 7px;
                    border-radius: 999px;
                    background: #d32f2f;
                    color: #fff;
                    font-size: 0.72rem;
                    font-weight: 800;
                    line-height: 22px;
                    text-align: center;
                    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.12);
                }
            }

            @keyframes pendingPulse {
                0%   { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.45); }
                70%  { box-shadow: 0 0 0 8px rgba(211, 47, 47, 0); }
                100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); }
            }
        }

        /* FOOTER */
        .foot-not {
            width: 100%;
            padding: 10px 6px;

            display: flex;
            justify-content: space-around;
            align-items: center;

            background: rgba(0, 0, 0, 0.2);
            border-top: 1px solid rgba(214, 170, 58, 0.2);

            i {
                font-size: 1.1rem;
                color: rgba(248, 244, 232, 0.8);
                transition: 0.25s ease;

                &:hover {
                    color: #f3cd61;
                    transform: scale(1.1);
                }
            }
        }
    }

    /* MOBILE */
    @media (max-width: 576px) {
        .menu-notif {
            width: 100%;
        }

        .titular-notif {
            min-height: 50px;

            .titulo-notif-mnu {
                font-size: 1.05rem;
            }
        }

        .opc-mnu {
            padding: 8px;

            .opc-mnu-not {
                min-height: 48px;
                font-size: 0.9rem;
                padding: 0 12px;

                i {
                    width: 32px;
                    height: 32px;
                }
            }
        }

        .foot-not {
            padding: 8px 4px;
        }
    }
`;